import { Attr, BelongsTo, HasMany, Model } from "spraypaint";

import { ApplicationRecord } from "shared/services/spraypaint";
import type { Nullable } from "shared/types";

import type Organisation from "./Organisation";
import type OrganisationTeam from "./OrganisationTeam";
import type OutreachCommunicationUser from "./OutreachCommunicationUser";
import type Role from "./Role";
import type StreamsUser from "./StreamsUser";
import type UserRequest from "./UserRequest";
import UsersOrganisationTeam from "./UsersOrganisationTeam";
import type XeroAuthorisation from "./XeroAuthorisation";

@Model()
class User extends ApplicationRecord {
  static jsonapiType = "users";

  @Attr() accessLevel: string;

  @Attr() active: boolean;

  @Attr() admin: string[];

  @Attr() briefSetIds: number[];

  @Attr({ persist: false }) createdAt: string;

  @Attr() cslRole: string;

  @Attr({ persist: false }) currencyCode: Nullable<string>;

  @Attr() currentSignInAt: string;

  @Attr() datagroupIds: number[];

  @Attr() deletionWarnings: string[];

  @Attr() email: string;

  @Attr() externalId: number;

  @Attr() externalType: string;

  @Attr() firstName: string;

  @Attr() hidden: boolean;

  @Attr() highValue: boolean;

  @Attr() identityProvider: string;

  @Attr() intercomVisibility: string;

  @Attr() language: string;

  @Attr() lastName: string;

  @Attr({ persist: false }) launchpadBeta: boolean;

  @Attr({ persist: false }) mentionStreamCount: number;

  @Attr() name: string;

  @Attr() notes: string;

  @BelongsTo() operationalTeam: OrganisationTeam;

  @Attr() operationalTeamId: number;

  @BelongsTo() organisation: Organisation;

  @Attr() organisationId: number;

  @Attr() organisationTeams: UsersOrganisationTeam[];

  @Attr({ persist: false }) other_organisations: Organisation[];

  @HasMany() outreachCommunicationUsers: OutreachCommunicationUser[];

  @Attr({ persist: false }) outreachPermissions: string[];

  @BelongsTo() outreachRole: Role;

  @Attr() outreachRoleId: number;

  @Attr({ persist: false }) permissions: string[];

  @Attr() phoneCountryCode: string;

  @Attr() phoneNumber: string;

  @Attr() pictureUrl: string;

  @BelongsTo() primaryOrganisationTeam: OrganisationTeam;

  @BelongsTo()
  primaryUsersOrganisationTeam: UsersOrganisationTeam;

  @Attr() qcModerator: boolean;

  @Attr() requests: UserRequest[];

  @BelongsTo() role: Role;

  @Attr() roleId: number;

  @Attr() secondaryTeamIds: number[];

  @Attr({ persist: false }) secondaryTeamNames: string[];

  @Attr() sentimentModerator: boolean;

  @Attr() signInCount: number;

  @Attr({ persist: false }) socialPermissions: string[];

  @BelongsTo() socialRole: Role;

  @Attr() socialRoleId: number;

  @Attr({ persist: false }) socialStreamCount: number;

  @HasMany() streamsUsers: StreamsUser[];

  @Attr() teamName: string;

  @Attr() timeZone: string;

  @Attr() transferableAssociations: boolean;

  @Attr() twoFactorAuthCarrier: string;

  @Attr() twoFactorAuthEnabled: boolean;

  @HasMany() userRequest: UserRequest[];

  @Attr() welcomeSentAt: string;

  @HasMany() xeroAuthorisations: XeroAuthorisation[];

  @Attr() xeroOauth2AuthorisationUrl: string;

  static active() {
    return this.where({ active: true });
  }

  assignPrimaryTeam(teamId: number) {
    if (teamId) {
      if (this.primaryUsersOrganisationTeam) {
        this.primaryUsersOrganisationTeam.organisationTeamId = teamId;
      } else {
        this.primaryUsersOrganisationTeam = new UsersOrganisationTeam({
          organisationTeamId: teamId,
          primary: true,
        });
      }
    } else if (this.primaryUsersOrganisationTeam) {
      this.primaryUsersOrganisationTeam.isMarkedForDestruction = true;
    }
  }

  primaryOrganisationTeamId() {
    return (
      this.primaryUsersOrganisationTeam?.organisationTeamId ||
      this.primaryOrganisationTeam?.id
    );
  }

  primaryOrganisationTeamName(defaultValue: string) {
    return this.primaryOrganisationTeam?.name || defaultValue;
  }
}

export default User;
